import numeral from "numeral"
import React, {useEffect, useState} from "react"
import {Link, useNavigate, useParams} from "react-router-dom"
import {useToasts} from "react-toast-notifications"
import Admonition from "../../components/Admonitions/Admonition"
import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import Back from "../../components/Back/Back"
import Loading from "../../components/Loading/Loading"
import TitleBar from "../../components/TitleBar/TitleBar"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import Card from "../../containers/Card/Card"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {
	archiveBill,
	Bill,
	BillEditInclude,
	BillPaymentMethod,
	BillPaymentMethodPayload,
	canArchiveBill,
	canCancelCheck,
	cancelBillCheck,
	CHECK_CANCEL_DELAY_DAYS,
	findVendor,
	getBill,
	getBillDeductionAccountId,
	getVendorName,
	isBillEditableStatus,
	isBillRepayableStatus,
	isBillRefundable,
	repayBill,
	refundBill,
	updateBill,
	Vendor,
} from "../../resources/bills"
import {Address, ErrorDocument, OkDocument} from "../../resources/common"
import {useAccessToken, useIsUnitAdminUser} from "../../services/auth"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"

const paymentMethodOptions: {value: BillPaymentMethod; label: string}[] = [
	{value: BillPaymentMethod.Ach, label: "ACH"},
	{value: BillPaymentMethod.SameDayAch, label: "Same-Day ACH"},
	{value: BillPaymentMethod.Check, label: "Check"},
	{value: BillPaymentMethod.External, label: "External"},
]

const notFilled = "Not filled"

function isPlatformPaymentMethod(
	method: BillPaymentMethod
): method is BillPaymentMethod.Ach | BillPaymentMethod.SameDayAch | BillPaymentMethod.Check {
	return (
		method === BillPaymentMethod.Ach || method === BillPaymentMethod.SameDayAch || method === BillPaymentMethod.Check
	)
}

function isAchLike(method: BillPaymentMethod) {
	return method === BillPaymentMethod.Ach || method === BillPaymentMethod.SameDayAch
}

function last4(value?: string) {
	if (!value) {
		return undefined
	}
	return value.slice(-4)
}

function formatAddress(address?: Address) {
	if (!address) {
		return undefined
	}

	const formatted = [address.street, address.street2, address.city, address.state, address.postalCode, address.country]
		.filter(Boolean)
		.join(", ")

	return formatted || undefined
}

function formatMaskedValue(name?: string, number?: string) {
	const digits = last4(number)
	if (!name && !digits) {
		return notFilled
	}

	return [name, digits ? `• ${digits}` : undefined].filter(Boolean).join(" ")
}

function formatVendorAccount(vendor?: Vendor) {
	const ach = vendor?.attributes?.paymentMethods?.ach
	if (!ach) {
		return notFilled
	}

	return formatMaskedValue(ach.name, ach.accountNumber)
}

function formatCheckDeliveryAddress(vendor?: Vendor) {
	return formatAddress(vendor?.attributes?.paymentMethods?.check?.address) || notFilled
}

function parseExistingPaymentMethod(paymentMethod?: BillPaymentMethodPayload) {
	if (!paymentMethod) {
		return {
			method: BillPaymentMethod.Ach,
			deductionAccount: undefined,
			deductionDate: "",
			paymentDate: "",
		}
	}

	if (BillPaymentMethod.Ach in paymentMethod) {
		return {
			method: BillPaymentMethod.Ach,
			deductionAccount: paymentMethod.ach.deductionAccount,
			deductionDate: paymentMethod.ach.deductionDate ?? "",
			paymentDate: "",
		}
	}
	if (BillPaymentMethod.SameDayAch in paymentMethod) {
		return {
			method: BillPaymentMethod.SameDayAch,
			deductionAccount: paymentMethod.sameDayAch.deductionAccount,
			deductionDate: paymentMethod.sameDayAch.deductionDate ?? "",
			paymentDate: "",
		}
	}
	if (BillPaymentMethod.Check in paymentMethod) {
		return {
			method: BillPaymentMethod.Check,
			deductionAccount: paymentMethod.check.deductionAccount,
			deductionDate: paymentMethod.check.deductionDate ?? "",
			paymentDate: "",
		}
	}

	return {
		method: BillPaymentMethod.External,
		deductionAccount: undefined,
		deductionDate: "",
		paymentDate: paymentMethod.external.paymentDate ?? "",
	}
}

function buildPaymentMethodPayload(
	method: BillPaymentMethod,
	deductionAccountId: string | undefined,
	deductionDate: string,
	paymentDate: string
): BillPaymentMethodPayload {
	if (method === BillPaymentMethod.External) {
		return {
			external: {
				...(paymentDate ? {paymentDate} : {}),
			},
		}
	}

	const platformPaymentData = {
		...(deductionAccountId ? {deductionAccount: String(deductionAccountId)} : {}),
		...(deductionDate ? {deductionDate} : {}),
	}

	if (method === BillPaymentMethod.SameDayAch) {
		return {sameDayAch: platformPaymentData}
	}
	if (method === BillPaymentMethod.Check) {
		return {check: platformPaymentData}
	}
	return {ach: platformPaymentData}
}

function Footer({state}: {state: AsyncResultIdleRequestState<OkDocument<Bill>, ErrorDocument>}) {
	return (
		<div className="card-content">
			{state.match(
				() => (
					<button className="button is-success">Save</button>
				),
				() => (
					<button className="button is-success is-loading">Saving</button>
				),
				(_) => null,
				(err) => (
					<>
						<div className="has-text-danger">{`${err.errors[0].title} - ${err.errors[0].detail || ""}`}</div>
						<button className="button is-success">Save</button>
					</>
				)
			)}
		</div>
	)
}

function HorizontalField({
	label,
	children,
	isNormal = true,
}: {
	label: string
	children: React.ReactNode
	isNormal?: boolean
}) {
	return (
		<div className="field is-horizontal">
			<div className={`field-label ${isNormal ? "is-normal" : ""}`}>
				<label className="label">{label}</label>
			</div>
			<div className="field-body">
				<div className="control">{children}</div>
			</div>
		</div>
	)
}

function EditBillForm({bill, vendor}: {bill: Bill; vendor?: Vendor}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const existing = parseExistingPaymentMethod(bill.attributes.paymentMethod)
	const deductionAccountId = getBillDeductionAccountId(bill)
	const [method, setMethod] = useState(existing.method)
	const [deductionDate, setDeductionDate] = useState(existing.deductionDate)
	const [paymentDate, setPaymentDate] = useState(existing.paymentDate)
	const [state, update] = useAsyncResultIdle(updateBill)

	useEffect(() => {
		if (state.isOk()) {
			addToast("Bill updated successfully", {appearance: "success"})
			navigate(`/bills/${bill.id}`, {replace: true})
		}
	}, [state])

	const vendorAccountLabel = formatVendorAccount(vendor)
	const disabled = state.match(
		() => false,
		() => true,
		(_) => true,
		(_) => false
	)

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault()
				update(
					accessToken,
					bill.id,
					buildPaymentMethodPayload(method, deductionAccountId, deductionDate, paymentDate),
					bill.relationships.customer.data.id.toString()
				)
			}}
			style={{marginBlockEnd: "1rem"}}
		>
			<fieldset disabled={disabled}>
				<Card title="Bill">
					<div className="card-content">
						<HorizontalField label="Payment Method">
							<div className="select">
								<select value={method} onChange={(e) => setMethod(e.target.value as BillPaymentMethod)} required>
									{paymentMethodOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						</HorizontalField>
						{isPlatformPaymentMethod(method) && (
							<>
								{isAchLike(method) && (
									<HorizontalField label="Vendor Account" isNormal={false}>
										{vendor && vendorAccountLabel !== notFilled ? (
											<Link className="link" to={`/vendors/${vendor.id}`}>
												{vendorAccountLabel}
											</Link>
										) : (
											notFilled
										)}
									</HorizontalField>
								)}
								<HorizontalField label="Deduction Date">
									<input
										className="input"
										type="date"
										value={deductionDate}
										onChange={(e) => setDeductionDate(e.target.value)}
									/>
								</HorizontalField>
								{method === BillPaymentMethod.Check && (
									<HorizontalField label="Check Delivery Address" isNormal={false}>
										{formatCheckDeliveryAddress(vendor)}
									</HorizontalField>
								)}
							</>
						)}
						{method === BillPaymentMethod.External && (
							<>
								<HorizontalField label="Payment Date">
									<input
										className="input"
										type="date"
										value={paymentDate}
										onChange={(e) => setPaymentDate(e.target.value)}
									/>
								</HorizontalField>
								<Admonition
									type="is-warning"
									title="Note"
									message="After you save, this bill’s status will change to Marked as Paid."
								/>
							</>
						)}
					</div>
					<Footer state={state} />
				</Card>
			</fieldset>
		</form>
	)
}

function ArchiveBillCard({bill}: {bill: Bill}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [state, archive] = useAsyncResultIdle(archiveBill)
	const canArchive = canArchiveBill(bill.attributes.status)

	return (
		<>
			<Card title="Archive this Bill" nested>
				<div className="card-content">
					{canArchive ? (
						<p>This action cannot be undone.</p>
					) : (
						<Admonition
							type="is-info"
							title="Archive is unavailable"
							message="A bill can be archived when its status is Draft or Scheduled."
						/>
					)}
				</div>
				<div className="card-content">
					<button
						className="button is-danger"
						type="button"
						disabled={!canArchive}
						onClick={() => setIsConfirmOpen(true)}
					>
						Archive
					</button>
				</div>
			</Card>
			{isConfirmOpen && (
				<AsyncResultModal
					title="Archive this Bill"
					close={() => setIsConfirmOpen(false)}
					buttonClassname="is-danger"
					buttonText="Archive"
					cancelButtonText="Cancel"
					state={state}
					successText="Bill archived successfully"
					onSubmit={() => archive(accessToken, bill.id)}
					onSuccess={() => {
						addToast("Bill archived successfully", {appearance: "success"})
						navigate(`/bills/${bill.id}`, {replace: true})
					}}
					errorToText={(err) => `${err.errors[0].title} - ${err.errors[0].detail || ""}`}
				>
					<p>Are you sure you want to archive this bill? This action cannot be undone.</p>
				</AsyncResultModal>
			)}
		</>
	)
}

function CancelCheckCard({bill}: {bill: Bill}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [state, cancelCheck] = useAsyncResultIdle(cancelBillCheck)
	const billNumber = bill.attributes.invoiceNumber || bill.id
	const canCancel = canCancelCheck(bill)

	return (
		<>
			<Card title="Cancel Check" nested>
				<div className="card-content">
					{canCancel ? (
						<p>
							This will cancel the check payment for bill #{billNumber}. The bill will return to Draft. This action
							cannot be undone.
						</p>
					) : (
						<Admonition
							type="is-info"
							title="Cancel check is unavailable"
							message={`A check can be canceled when the payment method is Check, the status is Funds Pushed, and more than ${CHECK_CANCEL_DELAY_DAYS} days have passed since the deduction date.`}
						/>
					)}
				</div>
				<div className="card-content">
					<button
						className="button is-danger"
						type="button"
						disabled={!canCancel}
						onClick={() => setIsConfirmOpen(true)}
					>
						Cancel Check
					</button>
				</div>
			</Card>
			{isConfirmOpen && (
				<AsyncResultModal
					title="Cancel Check"
					close={() => setIsConfirmOpen(false)}
					buttonClassname="is-danger"
					buttonText="Yes, Cancel Check"
					cancelButtonText="Do Not Cancel Check"
					state={state}
					successText={`Check #${billNumber} cancelled successfully`}
					onSubmit={() => cancelCheck(accessToken, bill.id)}
					onSuccess={() => {
						addToast(`Check #${billNumber} cancelled successfully`, {appearance: "success"})
						navigate(`/bills/${bill.id}`, {replace: true})
					}}
					errorToText={(err) => `${err.errors[0].title} - ${err.errors[0].detail || ""}`}
				>
					<p>
						You are about to cancel this check payment for bill #{billNumber}. The bill will return to Draft, and you
						will be able to set a new payment method or get a refund. This action cannot be undone.
					</p>
				</AsyncResultModal>
			)}
		</>
	)
}

function RepayBillCard({bill}: {bill: Bill}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [state, repay] = useAsyncResultIdle(repayBill)
	const canRepay = isBillRepayableStatus(bill.attributes.status)

	return (
		<>
			<Card title="Repay bill" nested>
				<div className="card-content">
					{canRepay ? (
						<p>This will retry collecting funds and paying the vendor for this bill.</p>
					) : (
						<Admonition
							type="is-info"
							title="Repay is unavailable"
							message="A bill can be repaid when its status is Canceled, Vendor Payment Failed, or Funds Deduction Blocked."
						/>
					)}
				</div>
				<div className="card-content">
					<button
						className="button is-success"
						type="button"
						disabled={!canRepay}
						onClick={() => setIsConfirmOpen(true)}
					>
						Repay
					</button>
				</div>
			</Card>
			{isConfirmOpen && (
				<AsyncResultModal
					title="Repay this bill?"
					close={() => setIsConfirmOpen(false)}
					buttonClassname="is-success"
					buttonText="Yes, repay"
					cancelButtonText="Cancel"
					state={state}
					successText="Bill repaid successfully"
					onSubmit={() => repay(accessToken, bill.id)}
					onSuccess={() => {
						addToast("Bill repaid successfully", {appearance: "success"})
						navigate(`/bills/${bill.id}`, {replace: true})
					}}
					errorToText={(err) => `${err.errors[0].title} - ${err.errors[0].detail || ""}`}
				>
					<p>Are you sure you want to repay this bill? This will retry collecting funds and paying the vendor.</p>
				</AsyncResultModal>
			)}
		</>
	)
}

function RefundBillCard({bill, vendor}: {bill: Bill; vendor?: Vendor}) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [state, refund] = useAsyncResultIdle(refundBill)
	const amount = numeral(bill.attributes.total / 100).format("$0,0.00")
	const destinationAccountId = getBillDeductionAccountId(bill)
	const vendorLabel = vendor ? getVendorName(vendor) : bill.relationships.vendor?.data.id
	const canRefund = isBillRefundable(bill)

	return (
		<>
			<Card title="Refund bill" nested>
				<div className="card-content">
					{canRefund ? (
						<p>
							This will refund the bill amount to the deduction account. Funds typically land in about 5 business days.
						</p>
					) : (
						<Admonition
							type="is-info"
							title="Refund is unavailable"
							message="A bill can be refunded when its status is Funds Deduction Blocked; when it is a check in Vendor Payment Failed, Funds Deduction Blocked, or Canceled; or when Deduction Payment Failed, Vendor Payment Failed, or Funds Deduction Blocked was caused by wrong account details."
						/>
					)}
				</div>
				<div className="card-content">
					<button
						className="button is-success"
						type="button"
						disabled={!canRefund}
						onClick={() => setIsConfirmOpen(true)}
					>
						Refund
					</button>
				</div>
			</Card>
			{isConfirmOpen && (
				<AsyncResultModal
					title="Refund this bill?"
					close={() => setIsConfirmOpen(false)}
					buttonClassname="is-success"
					buttonText="Yes, refund"
					cancelButtonText="Cancel"
					state={state}
					successText="Bill refund initiated successfully"
					onSubmit={() => refund(accessToken, bill.id)}
					onSuccess={() => {
						addToast("Bill refund initiated successfully", {appearance: "success"})
						navigate(`/bills/${bill.id}`, {replace: true})
					}}
					errorToText={(err) => `${err.errors[0].title} - ${err.errors[0].detail || ""}`}
				>
					<p>
						Refund {amount} for bill #{bill.id}
						{vendorLabel ? ` (${vendorLabel})` : ""}
						{destinationAccountId ? ` to account ${destinationAccountId}` : ""}.
					</p>
					<p>Are you sure you want to refund this bill?</p>
				</AsyncResultModal>
			)}
		</>
	)
}

export default function EditBillPage() {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const isUnitAdmin = useIsUnitAdminUser()
	const {billId = ""} = useParams<{billId: string}>()
	const billAsyncResult = useAsyncResult(() => getBill(accessToken, billId, BillEditInclude), [billId])

	useEffect(() => {
		if (!isUnitAdmin) {
			navigate(`/bills/${billId}`, {replace: true})
		}
	}, [billId, isUnitAdmin, navigate])

	if (!isUnitAdmin) {
		return <Loading />
	}

	return (
		<AsyncResultComponent asyncResult={billAsyncResult} pendingComponent={<Loading />}>
			{({value: billResponse}) => {
				const bill = billResponse.data
				const included = billResponse.included ?? []
				const vendorId = bill.relationships.vendor?.data.id
				const vendor = vendorId ? findVendor(vendorId, included) : undefined

				return (
					<>
						<TitleBar
							title={`Edit Bill #${billId}`}
							breadcrumbs={[{text: `Bill #${billId}`, path: `/bills/${billId}`}]}
						/>
						<MainSection>
							<div className="columns">
								<div className="column">
									<div className="buttons">
										<Back />
									</div>
								</div>
							</div>
							{isBillEditableStatus(bill.attributes.status) && <EditBillForm bill={bill} vendor={vendor} />}
							<div className="columns is-multiline bill-edit-action-columns">
								<div className="column is-half">
									<RepayBillCard bill={bill} />
								</div>
								<div className="column is-half">
									<ArchiveBillCard bill={bill} />
								</div>
								<div className="column is-half">
									<CancelCheckCard bill={bill} />
								</div>
								<div className="column is-half">
									<RefundBillCard bill={bill} vendor={vendor} />
								</div>
							</div>
						</MainSection>
					</>
				)
			}}
		</AsyncResultComponent>
	)
}
